import React, { useEffect, useState } from 'react';
import { useStoreState, useStoreActions } from 'easy-peasy';
import { useTheme } from '@mui/material/styles';
import {
	Stack,
	Box,
	Typography,
	CircularProgress,
	Button,
	IconButton,
	Tooltip,
	Checkbox,
	Dialog,
	DialogTitle,
	TextField,
	TableContainer,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	TableSortLabel
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { ContentCopy } from '@mui/icons-material';
import TextWithCopyIcon from 'components7/TextWithCopyIcon';
import Chat from 'components/Chat';
import NumericStepper from 'components/NumericStepper';
import utils from 'components/utils';

const Details = props => {
	const { wishlistId, whislistName, wishlistType, expanded, viewAction, address } = props;
	const theme = useTheme();
	const translate = utils.useI18N();
	//const expanded = useStoreState(state => state.wishlistSpecial.expanded);
	const wishlistDetail = useStoreState(state => state.wishlistSpecial.wishlistDetail);
	const loadingWishlistDetail = useStoreState(state => state.wishlistSpecial.loadingWishlistDetail);
	const loadWishlistDetail = useStoreActions(dispatch => dispatch.wishlistSpecial.loadWishlistDetail);
	const loadingAction = useStoreState(state => state.wishlistSpecial.loadingAction);
	const order = useStoreState(state => state.wishlistSpecial.orderDirection);
	const orderBy = useStoreState(state => state.wishlistSpecial.orderBy);
	const setOrder = useStoreActions(dispatch => dispatch.wishlistSpecial.setOrder);
	const reject = useStoreActions(dispatch => dispatch.wishlistSpecial.reject);
	const accept = useStoreActions(dispatch => dispatch.wishlistSpecial.accept);
	const allSelection = useStoreActions(dispatch => dispatch.wishlistSpecial.allSelection);
	const removeItem = useStoreActions(dispatch => dispatch.wishlistSpecial.removeItem);
	const updateItem = useStoreActions(dispatch => dispatch.wishlistSpecial.updateItem);
	const openUpdateQta = useStoreState(state => state.wishlistSpecial.openUpdateQta);
	const setOpenUpdateQta = useStoreActions(dispatch => dispatch.wishlistSpecial.setOpenUpdateQta);
	const [notes, setNotes] = useState('');
	const [textAreaCount, settextAreaCount] = useState(100);
	const [openAccept, setOpenAccept] = useState(false);
	const [openReject, setOpenReject] = useState(false);
	const [updateQtaItem, setUpdateQtaItem] = useState({ id: 0, qta: 0 });

	const columns = [
		{ id: 'ean', headerName: translate('wishlist.tables.ean'), width: 120, editable: true },
		{ id: 'title', headerName: translate('wishlist.tables.title'), width: 150, editable: true },
		{ id: 'serie', headerName: translate('wishlist.tables.serie'), width: 110, editable: true },
		{ id: 'publisher', headerName: translate('wishlist.tables.publisher'), width: 110, editable: true },
		{ id: 'price', headerName: translate('wishlist.tables.price'), width: 90, editable: true },
		{ id: 'depot', headerName: translate('wishlist.tables.depot'), width: 90, editable: true },
		{ id: 'quantity', headerName: translate('wishlist.tables.quantity'), width: 110, editable: true }
	];

	//const rows = wishlistDetail ? wishlistDetail : [];
	const sortedRows = utils.stableSort(wishlistDetail, order, orderBy);
	const itemSelected = wishlistDetail.filter(i => i.selected);
	//const disableButton = wishlistType == 'PRESENTAZIONE' && wishlistDetail.filter(i => i.quantity >= 10).length == 0; -> Gli utenti approvatori possono gestire le quantità a loro discrezione indipendentemente dai vincoli

	const getMinValueSpecial = _ => {
		if (wishlistType) {
			if (wishlistType === 'ADOZIONE') return 15;
			if (wishlistType === 'FESTIVAL') return 3;
			//Nel caso della presentazione verifico se le quantità degli altri titoli
			if (wishlistType === 'PRESENTAZIONE') return 1;

			return 1;
		}
		return 1;
	};

	const recalculate = e => {
		const charCount = e.target.value.length;
		const charLeft = 100 - charCount;
		settextAreaCount(charLeft);
	};

	const handleOpenAccept = event => {
		setOpenAccept(true);
		setOpenReject(false);
	};

	const handleOpenReject = event => {
		setOpenReject(true);
		setOpenAccept(false);
	};

	const handleCloseAcceptReject = event => {
		setOpenAccept(false);
		setOpenReject(false);
	};

	const acceptWishlist = event => {
		accept({ id: wishlistId, notes: notes });
	};

	const rejectWishlist = event => {
		reject({ id: wishlistId, notes: notes });
	};

	const handleAllSelection = event => {
		allSelection(event.target.checked);
	};

	const handleOpenUpdateQta = (id, value) => {
		setUpdateQtaItem({ id: id, value: value });
		setOpenUpdateQta(true);
	};

	const handleCloseUpdateQta = event => {
		setOpenUpdateQta(false);
	};

	const onStepperChange = valueAsNumber => {
		const limitValue = getMinValueSpecial();
		const newValue = valueAsNumber < limitValue ? limitValue : valueAsNumber;

		setUpdateQtaItem({ id: updateQtaItem.id, value: newValue });
	};

	const handleUpdateQta = event => {
		updateItem({
			wishlistItemId: updateQtaItem.id,
			quantity: updateQtaItem.value,
			depotCode: null,
			depotDescription: null,
			notes: null
		});
	};

	useEffect(() => {
		loadWishlistDetail(expanded.id);
	}, [expanded]);

	return (
		<Stack
			direction="column"
			justifyContent="flex-start"
			alignItems="flex-start"
			spacing={0}
			sx={{ width: '100%' }}
		>
			{loadingWishlistDetail && (
				<Stack
					direction="column"
					justifyContent="center"
					alignItems="center"
					spacing={0}
					sx={{ width: '100%' }}
				>
					<CircularProgress size={24} />
				</Stack>
			)}
			{!loadingWishlistDetail && (
				<Stack
					direction="column"
					justifyContent="flex-start"
					alignItems="flex-start"
					spacing={0}
					sx={{ width: '100%' }}
				>
					{address && <TextWithCopyIcon label={translate('user.address.street')} text={address} />}
					<Stack
						direction="row"
						justifyContent={viewAction ? 'space-between' : 'flex-end'}
						alignItems="center"
						spacing={0}
						sx={{ width: '100%', margin: '10px 0 !important' }}
					>
						{viewAction && (
							<Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={1}>
								<LoadingButton
									variant="dark"
									//disabled={disableButton}
									onClick={handleOpenAccept}
									endIcon={<ThumbUpAltIcon />}
									loading={loadingAction}
									loadingPosition="end"
								>
									{translate('wishlistSpecial.accept')}
								</LoadingButton>
								<LoadingButton
									variant="dark"
									//disabled={disableButton}
									onClick={handleOpenReject}
									endIcon={<ThumbDownAltIcon />}
									loading={loadingAction}
									loadingPosition="end"
									sx={{ backgroundColor: theme.palette.secondary.main }}
								>
									{translate('wishlistSpecial.reject')}
								</LoadingButton>
								<Dialog open={openAccept || openReject} onClose={handleCloseAcceptReject}>
									<DialogTitle
										sx={{
											textAlign: 'left',
											borderBottom: '1px solid #cecece',
											padding: '0px !Important'
										}}
									>
										<Typography variant="titleTab">
											{openAccept
												? translate('wishlistSpecial.accept')
												: openReject
												? translate('wishlistSpecial.reject')
												: ''}
											{translate('wishlistSpecial.handleAcceptReject')}
										</Typography>
									</DialogTitle>
									<Stack
										direction="column"
										justifyContent="flex-start"
										alignItems="flex-start"
										spacing={2}
										sx={{
											width: { xs: '320px', md: '600px', sm: '600px' },
											padding: '10px !important'
										}}
									>
										<Typography variant="textTable" sx={{ marginBottom: '0px' }}>
											{whislistName}
										</Typography>
										<Stack
											direction="column"
											justifyContent="flex-start"
											alignItems="flex-start"
											spacing={0.5}
											sx={{ width: '100%' }}
										>
											<TextField
												multiline
												maxRows={5}
												rows={3}
												maxLength={100}
												placeholder="note"
												defaultValue={notes}
												onChange={e => {
													setNotes(e.target.value);
													recalculate(e);
												}}
												sx={{ width: '100%' }}
											/>
											<Typography variant="textTable">Max {textAreaCount} crt.</Typography>
										</Stack>
										<Stack
											direction="row"
											justifyContent="center"
											alignItems="center"
											spacing={1}
											sx={{ width: '100%' }}
										>
											<Button variant="light" onClick={handleCloseAcceptReject}>
												{translate('common.cancel')}
											</Button>
											<LoadingButton
												variant="dark"
												onClick={
													openAccept
														? acceptWishlist
														: openReject
														? rejectWishlist
														: handleCloseAcceptReject
												}
												endIcon={
													openAccept ? (
														<ThumbUpAltIcon />
													) : openReject ? (
														<ThumbDownAltIcon />
													) : (
														'none'
													)
												}
												loading={loadingAction}
												loadingPosition="end"
												sx={{
													backgroundColor: openAccept
														? theme.palette.primary.main
														: openReject
														? theme.palette.secondary.main
														: 'inherit'
												}}
											>
												{openAccept
													? translate('wishlistSpecial.accept')
													: openReject
													? translate('wishlistSpecial.reject')
													: ''}
											</LoadingButton>
										</Stack>
									</Stack>
								</Dialog>
							</Stack>
						)}
						<Chat type="WISHLISTSPECIAL" id={wishlistId} titleChat={whislistName} />
					</Stack>
					<TableContainer>
						<Table size="small" stickyHeader aria-label="sticky table">
							<TableHead>
								<TableRow>
									{viewAction && sortedRows.length > 1 && (
										<TableCell sx={{ background: theme.palette.custom.greytable }} width="40px">
											{/*
										<Checkbox
											indeterminate={itemSelected.length > 0 && itemSelected.length < wishlistDetail.length}
											checked={itemSelected.length > 0 && itemSelected.length === wishlistDetail.length}
											onChange={handleAllSelection}
										/>
										*/}
										</TableCell>
									)}
									{columns.map((col, index) => (
										<TableCell
											key={col.id}
											sortDirection={orderBy === col.id ? order : false}
											sx={{ background: theme.palette.custom.greytable }}
										>
											<TableSortLabel
												active={orderBy === col.id}
												direction={orderBy === col.id ? order : 'asc'}
												onClick={e => setOrder(col.id)}
											>
												{col.headerName}
											</TableSortLabel>
										</TableCell>
									))}
								</TableRow>
							</TableHead>
							<TableBody>
								{sortedRows.map((row, index) => (
									<TableRow key={index}>
										{viewAction && sortedRows.length > 1 && (
											<TableCell>
												{loadingAction && <CircularProgress size={24} />}
												{!loadingAction && (
													<IconButton
														onClick={_ => {
															removeItem(row.id);
														}}
													>
														<CloseIcon sx={{ color: theme.palette.secondary.main }} />
													</IconButton>
												)}
											</TableCell>
										)}
										<TableCell>
											<Typography variant="textTable">{row.ean}</Typography>
										</TableCell>
										<TableCell>
											<Typography variant="textTable">{row.title}</Typography>
										</TableCell>
										<TableCell>
											<Typography variant="textTable">{row.serie}</Typography>
										</TableCell>
										<TableCell>
											<Typography variant="textTable">{row.publisher}</Typography>
										</TableCell>
										<TableCell>
											<Typography variant="textTable">
												{utils.formatPrice(row.fullPrice)}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography variant="textTable">{row.depotDescription}</Typography>
										</TableCell>
										<TableCell>
											<Stack
												direction="row"
												justifyContent="flex-start"
												alignItems="center"
												spacing={0}
											>
												<Typography variant="textTable">{row.quantity}</Typography>
												{viewAction && (
													<IconButton
														onClick={_ => handleOpenUpdateQta(row.id, row.quantity)}
													>
														<ModeEditIcon
															sx={{ color: theme.palette.primary.main, width: '20px' }}
														/>
													</IconButton>
												)}
											</Stack>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
					{/** Dialog modifica quantità */}
					<Dialog open={openUpdateQta} onClose={handleCloseUpdateQta}>
						<DialogTitle
							sx={{
								textAlign: 'left',
								borderBottom: '1px solid #cecece',
								padding: '0px !Important'
							}}
						>
							<Typography variant="titleTab">{translate('wishlistSpecial.updateQta')}</Typography>
						</DialogTitle>
						<Stack
							direction="column"
							justifyContent="flex-start"
							alignItems="flex-start"
							spacing={2}
							sx={{
								width: { xs: '300px', md: '300px', sm: '300px' },
								padding: '10px !important'
							}}
						>
							<Typography variant="textTable" sx={{ marginBottom: '0px' }}>
								{whislistName}
							</Typography>
							<Box sx={{ maxWidth: '90px' }}>
								<NumericStepper
									removeDot
									precision={0}
									min={getMinValueSpecial()}
									max={999}
									step={1}
									value={updateQtaItem.value}
									onChange={onStepperChange}
								/>
							</Box>
							<Stack
								direction="row"
								justifyContent="center"
								alignItems="center"
								spacing={1}
								sx={{ width: '100%' }}
							>
								<Button variant="light" onClick={handleCloseUpdateQta}>
									{translate('common.cancel')}
								</Button>
								<LoadingButton
									variant="dark"
									onClick={handleUpdateQta}
									endIcon={<SaveIcon />}
									loading={loadingAction}
									loadingPosition="end"
								>
									{translate('common.save')}
								</LoadingButton>
							</Stack>
						</Stack>
					</Dialog>
				</Stack>
			)}
		</Stack>
	);
};

export default Details;
